import { useEffect, useRef } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { ChartSettings } from '../components/SettingsModal'
import {
  DILUTION_PRICE_LINE_NAME,
  DILUTION_LINE_COLOR,
  type DilutionLevel,
} from '../utils/dilutionPriceLine'

export type { DilutionLevel }

type Chart = ReturnType<ChartManager['getChart']>

/**
 * Mounts / updates / removes the read-only "dilution level" overlays — one
 * locked, full-width dashed line (plus a y-axis price pill) per in-play warrant
 * exercise price OR live S-3 offering price. Declarative twin of
 * {@link useEarningsMarkers}: keyed on the chart instance so a late instance OR
 * late data both trigger a clean (re)draw, with no imperative one-shot to miss.
 * Remove-first every run for a clean slate, then recreate; overlay ids are
 * tracked in a ref because klinecharts removes overlays by id. The overlay
 * TEMPLATE is registered once at EdgeChart module load (like the earnings
 * marker overlay), so this hook only creates/removes instances.
 *
 * Each level's `kind` ('warrant' default, or 'offering') selects its own gate
 * (`showDilutionLevels` for warrants, `showOfferingLevels` for offerings), so a
 * host can concatenate both families into one `levels` array and flip either
 * toggle independently; a level's `color` (falling back to
 * {@link DILUTION_LINE_COLOR}) keeps the two families visually distinct.
 *
 * Levels are only supplied once the host's warrant/offering fetch resolves
 * (gated behind the same `barsReady` signal as earnings), so overlays are
 * always created after bars have rendered — which is why the old
 * `pendingDilutionPriceLines` parking in ChartManager is no longer needed.
 */
export function useDilutionLines(
  chartInstance: Chart | null,
  settings: ChartSettings,
  levels: DilutionLevel[] | undefined,
): void {
  const showDilutionLevels = settings.showDilutionLevels
  const showOfferingLevels = settings.showOfferingLevels
  // Stable identity for the deps array without re-running on array reference
  // churn: the levels payload is tiny, so a JSON key is fine (mirrors
  // useEarningsMarkers' earningsKey).
  const levelsKey = levels ? JSON.stringify(levels) : ''
  const idsRef = useRef<string[]>([])

  useEffect(() => {
    if (!chartInstance) return

    // Always remove first for a clean state on any change.
    for (const id of idsRef.current) chartInstance.removeOverlay(id)
    idsRef.current = []

    // "Ended" is judged as of the chart's latest bar (not the clock) so replay
    // and historical views read each level as it stood then. A warrant's end
    // can be a FUTURE expiry, which is still in play. No bars yet: the clock.
    const data = (
      chartInstance as { getDataList?: () => Array<{ timestamp: number }> }
    ).getDataList?.()
    const asOf = data && data.length > 0 ? data[data.length - 1].timestamp : Date.now()

    const visible = (levels ?? []).filter((level) =>
      level.kind === 'offering' ? showOfferingLevels : showDilutionLevels,
    )
    for (const level of visible) {
      const id =
        (chartInstance.createOverlay({
          name: DILUTION_PRICE_LINE_NAME,
          // Always two points so the overlay is complete under totalStep 3:
          // start (anchored at the event date when known) and end (the date
          // the level died; value-only while it is live, which the template
          // reads as open-ended, running to the right edge).
          points: [
            level.startTimestamp != null
              ? { timestamp: level.startTimestamp, value: level.price }
              : { value: level.price },
            // An end after the latest bar (a warrant's future expiry) is still
            // open here: klinecharts snaps a past-the-data timestamp to a bar
            // up to two before the last, which cut live lines short.
            level.endTimestamp != null && level.endTimestamp <= asOf
              ? { timestamp: level.endTimestamp, value: level.price }
              : { value: level.price },
          ],
          lock: true,
          extendData: {
            color: level.color ?? DILUTION_LINE_COLOR,
            label: level.label,
            // Ended levels keep their dated segment but drop the right-axis
            // price label, so the axis lists only prices still in play.
            ended: level.endTimestamp != null && level.endTimestamp <= asOf,
          },
        } as Parameters<Chart['createOverlay']>[0]) as string | null) ?? null
      if (id) idsRef.current.push(id)
    }
    // levelsKey stands in for the levels array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartInstance, showDilutionLevels, showOfferingLevels, levelsKey])
}
